import StickyTableResponsive from '../common/StickyTableResponsive';
import SortableTh from '../common/SortableTh';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import MasterLayout from '../../masterLayout/MasterLayout';
import Breadcrumb from '../Breadcrumb';
import { AppFilterBar, AppFilterInput } from '../filters';
import {
  AppButton,
  AppEmptyState,
  AppPagination,
  AppSpinner,
} from '../ui';
import useAppConfirm from '../../hooks/useAppConfirm';
import { useTableSort } from '../../hooks/useTableSort';
import { notify } from '../common/AppToast';
import '../../styles/kkp-planning-page.scss';

const DEFAULT_DELETE_CONFIRM = {
  title: 'Supprimer ?',
  message: 'Cette action est irréversible.',
  confirmLabel: 'Supprimer',
  confirmVariant: 'danger',
  successMessage: 'Supprimé',
};

/**
 * Page admin CRUD liste réutilisable (filtre, tableau triable, pagination, actions).
 *
 * load(page, search) => Promise<{ rows, pagination }>
 * columns: [{ key, label, sortable?, sortType?, getValue?, render(row) }]
 */
const AdminCrudListPage = ({
  title,
  subtitle,
  backTo,
  newTo,
  onNew,
  newLabel = 'Nouveau',
  headerExtra,
  search,
  columns = [],
  load,
  rowKey = 'id',
  getRowKey,
  rowClassName,
  onEdit,
  editTo,
  onDelete,
  deleteConfirm,
  extraActions,
  toolbarExtra,
  emptyMessage = 'Aucun résultat',
  debounceMs = 300,
  limit = 20,
}) => {
  const { ask, modal } = useAppConfirm();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 0, total: 0 });
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(true);
  const loadRef = useRef(load);
  loadRef.current = load;

  const searchEnabled = Boolean(search) && search.enabled !== false;
  const searchPlaceholder = typeof search === 'object' ? (search.placeholder || 'Rechercher…') : 'Rechercher…';
  const searchId = (typeof search === 'object' && search.id) || 'admin-crud-search';
  const searchLabel = (typeof search === 'object' && search.label) || 'Recherche';
  const searchCol = (typeof search === 'object' && search.col) || 'col-12 col-md-8 col-lg-6';

  const sortColumnDefs = useMemo(() => {
    const defs = {};
    columns.forEach((col) => {
      if (col.sortable === false || !col.key) return;
      defs[col.key] = {
        type: col.sortType || 'string',
        getValue: col.getValue || ((row) => row[col.key]),
      };
    });
    return defs;
  }, [columns]);

  const { sortedRows, sort, toggleSort, getSortIcon } = useTableSort(rows, sortColumnDefs);

  const resolveRowKey = useCallback((row, index) => {
    if (typeof getRowKey === 'function') return getRowKey(row, index);
    return row?.[rowKey] ?? index;
  }, [getRowKey, rowKey]);

  const fetchPage = useCallback(async (page = 1, q = searchValue) => {
    setLoading(true);
    try {
      const result = await loadRef.current(page, q, { limit });
      setRows(result?.rows ?? []);
      setPagination(result?.pagination ?? { page: 1, pages: 0, total: 0 });
    } catch (err) {
      notify.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [limit, searchValue]);

  useEffect(() => {
    if (!searchEnabled) {
      fetchPage(1);
      return undefined;
    }
    const timer = setTimeout(() => fetchPage(1), debounceMs);
    return () => clearTimeout(timer);
  }, [fetchPage, searchEnabled, debounceMs]);
  const handleNew = () => {
    if (typeof onNew === 'function') {
      onNew();
      return;
    }
    if (newTo) navigate(newTo);
  };

  const handleEdit = (row) => {
    if (typeof onEdit === 'function') {
      onEdit(row);
      return;
    }
    if (typeof editTo === 'function') {
      navigate(editTo(row));
    }
  };

  const showEdit = typeof onEdit === 'function' || typeof editTo === 'function';

  const handleDelete = async (row) => {
    if (typeof onDelete !== 'function') return;
    const conf = {
      ...DEFAULT_DELETE_CONFIRM,
      ...(typeof deleteConfirm === 'function' ? deleteConfirm(row) : deleteConfirm),
    };
    const ok = await ask({
      title: conf.title,
      message: conf.message,
      confirmLabel: conf.confirmLabel,
      confirmVariant: conf.confirmVariant,
    });
    if (!ok) return;
    try {
      await onDelete(row);
      notify.success(conf.successMessage);
      fetchPage(pagination.page);
    } catch (err) {
      notify.error(err.message);
    }
  };

  const reload = useCallback(() => fetchPage(pagination.page), [fetchPage, pagination.page]);

  const actionHelpers = useMemo(() => ({ reload, ask }), [reload, ask]);

  const hasActions = showEdit || typeof onDelete === 'function' || typeof extraActions === 'function';

  const breadcrumbActions = (
    <div className="d-flex gap-2 flex-wrap">
      {backTo ? (
        <AppButton as={Link} to={backTo} size="sm" variant="outline-secondary">
          Retour
        </AppButton>
      ) : null}
      {headerExtra}
      {(newTo || onNew) ? (
        <AppButton size="sm" icon="mdi:plus" onClick={handleNew}>
          {newLabel}
        </AppButton>
      ) : null}
    </div>
  );

  return (
    <MasterLayout>
      <Breadcrumb
        title={title}
        subtitle={subtitle}
        actions={breadcrumbActions}
      />

      {(searchEnabled || toolbarExtra) ? (
        <AppFilterBar
          onReset={searchEnabled ? () => setSearchValue('') : undefined}
          resetDisabled={searchEnabled ? !searchValue : true}
        >
          {searchEnabled ? (
            <AppFilterInput
              id={searchId}
              label={searchLabel}
              col={searchCol}
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={setSearchValue}
            />
          ) : null}
          {toolbarExtra}
        </AppFilterBar>
      ) : null}

      <div className="card">
        <div className="card-body p-0">
          {loading ? (
            <AppSpinner />
          ) : rows.length === 0 ? (
            <AppEmptyState message={emptyMessage} />
          ) : (
            <StickyTableResponsive>
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    {columns.map((col) => (
                      <SortableTh
                        key={col.key || col.label}
                        sortKey={col.key}
                        label={col.label}
                        sort={sort}
                        onSort={toggleSort}
                        getSortIcon={getSortIcon}
                        sortable={col.sortable !== false && Boolean(col.key)}
                      />
                    ))}
                    {hasActions ? (
                      <SortableTh label="Actions" sortable={false} />
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((row, index) => {
                    const className = typeof rowClassName === 'function'
                      ? rowClassName(row)
                      : rowClassName;
                    return (
                      <tr key={resolveRowKey(row, index)} className={className || undefined}>
                        {columns.map((col) => (
                          <td key={col.key || col.label}>
                            {typeof col.render === 'function'
                              ? col.render(row)
                              : (row[col.key] ?? '—')}
                          </td>
                        ))}
                        {hasActions ? (
                          <td>
                            <div className="d-flex gap-1">
                              {showEdit ? (
                                <AppButton
                                  size="sm"
                                  variant="outline-primary"
                                  icon="mdi:pencil"
                                  title="Modifier"
                                  onClick={() => handleEdit(row)}
                                />
                              ) : null}
                              {typeof extraActions === 'function' ? extraActions(row, actionHelpers) : null}
                              {typeof onDelete === 'function' ? (
                                <AppButton
                                  size="sm"
                                  variant="outline-danger"
                                  icon="mdi:delete"
                                  title="Supprimer"
                                  onClick={() => handleDelete(row)}
                                />
                              ) : null}
                            </div>
                          </td>
                        ) : null}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </StickyTableResponsive>
          )}
        </div>
      </div>

      <AppPagination
        pagination={pagination}
        search={searchEnabled ? searchValue : undefined}
        onPageChange={(p) => fetchPage(p)}
      />
      {modal}
    </MasterLayout>
  );
};

export default AdminCrudListPage;
